import { defineStore } from 'pinia'
import { collection, getDocs, query, where, orderBy, addDoc, doc, setDoc, deleteDoc } from 'firebase/firestore'
import { BaseStore } from './baseStore'
import { db } from '../firebase'
import { useUserStore } from './user'
import { useAuthStore } from './auth'
import { useCurrentOrganizationStore } from './currentOrganization'

export interface Account {
  id?: string
  description: string
  value: number
  dueDate: string
  type: 'payable' | 'receivable'
  organizationId: string
  entityId: string
  entityName?: string
  costCenterId?: string
  costTypeId?: string
  costCenterName?: string
  costTypeName?: string
  status: string
  notes?: string
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  updatedBy?: string
}

class AccountStore extends BaseStore {
  constructor() {
    super('accounts')
  }

  async createAccount(accountData: Account) {
    const docRef = await addDoc(collection(db, this.collectionName), {
      ...accountData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })
    
    return {
      id: docRef.id,
      ...accountData
    }
  }

  async updateAccount(id: string, accountData: Partial<Account>) {
    const docRef = doc(db, this.collectionName, id)
    await setDoc(docRef, {
      ...accountData,
      updatedAt: new Date().toISOString()
    }, { merge: true })
    
    return {
      id,
      ...accountData
    }
  }

  async deleteAccount(id: string) {
    try {
      const docRef = doc(db, this.collectionName, id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Erro ao deletar conta:', error)
      throw error
    }
  }
}

export const useAccountStore = defineStore('account', {
  state: () => ({
    accounts: [] as Account[],
    loading: false,
    error: null as string | null,
  }),

  getters: {
    getAccountsByType: (state) => (type: 'payable' | 'receivable') => {
      return state.accounts.filter(account => account.type === type)
    },
    
    getSortedAccounts: (state) => {
      return [...state.accounts].sort((a, b) => 
        new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
      )
    }
  },

  actions: {
    async createAccount(account: Account) {
      try {
        this.loading = true
        this.error = null
        
        const authStore = useAuthStore()
        const organizationStore = useCurrentOrganizationStore()
        const user = authStore.user
        
        if (!user) throw new Error('Usuário não encontrado')
        if (!organizationStore.currentOrganization) throw new Error('Organização não selecionada')
        
        const accountData = {
          ...account,
          organizationId: organizationStore.currentOrganization.id,
          createdBy: user.uid,
          updatedBy: user.uid,
          status: account.status || 'PENDENTE'
        }
        
        const accountStore = new AccountStore()
        const createdAccount = await accountStore.createAccount(accountData)
        
        // Adiciona a nova conta ao estado e ordena
        this.accounts = [...this.accounts, createdAccount].sort((a, b) => 
          new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
        )
        
        return createdAccount
      } catch (error: any) {
        console.error('Erro ao criar conta:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async deleteAccount(accountId: string) {
      try {
        this.loading = true;
        this.error = null;
        
        const organizationStore = useCurrentOrganizationStore();
        const currentOrganizationId = organizationStore.currentOrganization.id;
        
        if (!currentOrganizationId) {
          throw new Error('Organização não encontrada');
        }

        const docRef = doc(db, 'accounts', accountId);
        await deleteDoc(docRef);
        
        // Remove a conta do state local
        this.accounts = this.accounts.filter(account => account.id !== accountId);
        
        this.loading = false;
      } catch (error: any) {
        this.loading = false;
        this.error = error.message;
        throw error;
      }
    },

    async updateAccount(id: string, accountData: Partial<Account>) {
      try {
        this.loading = true
        this.error = null
        
        const authStore = useAuthStore()
        const user = authStore.user
        
        if (!user) throw new Error('Usuário não encontrado')
        
        const updatedData = {
          ...accountData,
          updatedBy: user.uid,
          updatedAt: new Date().toISOString()
        }
        
        const docRef = doc(db, 'accounts', id)
        await setDoc(docRef, updatedData, { merge: true })
        
        // Atualiza a conta no estado local
        this.accounts = this.accounts.map(account => 
          account.id === id ? { ...account, ...updatedData } : account
        )
        
        return { id, ...updatedData }
      } catch (error: any) {
        console.error('Erro ao atualizar conta:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchAccounts() {
      try {
        this.loading = true
        this.error = null
        
        const organizationStore = useCurrentOrganizationStore()
        if (!organizationStore.currentOrganization) {
          throw new Error('Organização não selecionada')
        }
        
        const q = query(
          collection(db, 'accounts'),
          where('organizationId', '==', organizationStore.currentOrganization.id)
        )
        
        const querySnapshot = await getDocs(q)
        const accounts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Account[]

        // Ordenação feita no cliente por enquanto
        this.accounts = accounts.sort((a, b) => 
          new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime()
        )
        
      } catch (error: any) {
        console.error('Erro ao buscar contas:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async removeDuplicateAccounts() {
      try {
        const accountGroups = new Map()
        
        this.accounts.forEach(account => {
          const key = `${account.description}-${account.value}-${account.dueDate}-${account.type}-${account.organizationId}-${account.entityName}`
          if (!accountGroups.has(key)) {
            accountGroups.set(key, [])
          }
          accountGroups.get(key).push(account)
        })

        let deletedCount = 0
        for (const accounts of accountGroups.values()) {
          if (accounts.length > 1) {
            // Mantém a conta mais antiga e remove as duplicatas
            accounts.sort((a, b) => new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime())
            
            for (let i = 1; i < accounts.length; i++) {
              if (accounts[i].id) {
                const accountStore = new AccountStore()
                await accountStore.deleteAccount(accounts[i].id)
                deletedCount++
              }
            }
          }
        }

        if (deletedCount > 0) {
          console.log(`Removidas ${deletedCount} contas duplicadas`)
          await this.fetchAccounts()
        }

        return deletedCount
      } catch (error) {
        console.error('Erro ao remover contas duplicadas:', error)
        throw error
      }
    }
  }
})
